import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Type, X, Trash2, Check, AtSign, Smile } from 'lucide-react';
import StoryStickerPicker from './StoryStickerPicker';

/**
 * StoryEditor — full-screen text-overlay editor for a story preview.
 *
 * Props:
 *   preview:    object URL of the selected file
 *   mediaType:  'image' | 'video'
 *   onCancel:   user clicked the close button
 *   onPublish:  user clicked publish; receives the list of overlay objects
 *   publishing: boolean — disables the publish button while uploading
 *
 * Overlay shape (sent to backend):
 *   { id, type:'text', text, x, y, scale, rotation, color, bg, font_size,
 *     font_family, mentions:[{username, user_id}] }
 *   x/y are NORMALIZED to the preview surface (0..1) so layout is independent
 *   of the rendering aspect ratio.
 */
const PALETTE = ['#ffffff', '#000000', '#ff3b30', '#ff9500', '#ffcc00', '#34c759', '#00c7be', '#007aff', '#af52de', '#ff2d92'];

const StoryEditor = ({ preview, mediaType, onCancel, onPublish, publishing }) => {
  const [overlays, setOverlays] = useState([]);
  const [editing, setEditing] = useState(null);     // overlay id currently being edited (text input modal)
  const [activeId, setActiveId] = useState(null);   // currently selected overlay (for color/size tweaks)
  const [showStickers, setShowStickers] = useState(false);
  const stageRef = useRef(null);

  const addSticker = (emoji) => {
    const id = `s_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const newO = {
      id, type: 'sticker', emoji,
      x: 0.5, y: 0.5, scale: 1.0, rotation: 0,
    };
    setOverlays(prev => [...prev, newO]);
    setActiveId(id);
    setShowStickers(false);
  };

  const addOverlay = () => {
    const id = `o_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const newO = {
      id, type: 'text', text: '',
      x: 0.5, y: 0.5, scale: 1.0, rotation: 0,
      color: '#ffffff', bg: 'none', font_size: 28, font_family: 'sans',
      mentions: [],
    };
    setOverlays(prev => [...prev, newO]);
    setEditing(id);
    setActiveId(id);
  };

  const updateOverlay = (id, patch) => {
    setOverlays(prev => prev.map(o => o.id === id ? { ...o, ...patch } : o));
  };

  const removeOverlay = (id) => {
    setOverlays(prev => prev.filter(o => o.id !== id));
    if (activeId === id) setActiveId(null);
    if (editing === id) setEditing(null);
  };

  // ---- DRAG (touch + mouse) ----
  const dragState = useRef({ active: false, id: null, startX: 0, startY: 0, originX: 0, originY: 0 });

  const onPointerDown = (e, id) => {
    if (editing) return;
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;
    setActiveId(id);
    const overlay = overlays.find(o => o.id === id);
    if (!overlay) return;
    const point = ('touches' in e && e.touches[0]) || e;
    dragState.current = {
      active: true, id,
      startX: point.clientX, startY: point.clientY,
      originX: overlay.x, originY: overlay.y,
    };
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragState.current.active) return;
      const rect = stageRef.current?.getBoundingClientRect();
      if (!rect) return;
      const point = ('touches' in e && e.touches[0]) || e;
      const dx = (point.clientX - dragState.current.startX) / rect.width;
      const dy = (point.clientY - dragState.current.startY) / rect.height;
      const id = dragState.current.id;
      const nx = Math.max(0.05, Math.min(0.95, dragState.current.originX + dx));
      const ny = Math.max(0.05, Math.min(0.95, dragState.current.originY + dy));
      setOverlays(prev => prev.map(o => o.id === id ? { ...o, x: nx, y: ny } : o));
      if (e.cancelable) e.preventDefault();
    };
    const onUp = () => { dragState.current.active = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  const handlePublish = () => {
    // Strip empty texts; keep meaningful overlays (text or sticker).
    const clean = overlays
      .filter(o => o.type === 'sticker' ? !!o.emoji : (o.text || '').trim().length > 0)
      .map(o => {
        if (o.type === 'sticker') {
          const { id, emoji, x, y, scale, rotation } = o;
          return { id, type: 'sticker', emoji, x, y, scale, rotation };
        }
        const { id, text, x, y, scale, rotation, color, bg, font_size, font_family, mentions } = o;
        return { id, type: 'text', text, x, y, scale, rotation, color, bg, font_size, font_family, mentions: mentions || [] };
      });
    onPublish(clean);
  };

  const editingOverlay = overlays.find(o => o.id === editing);
  const activeOverlay = overlays.find(o => o.id === activeId);

  return (
    <div className="fixed inset-0 bg-black z-[110] flex flex-col pt-[max(env(safe-area-inset-top),0px)] pb-[max(env(safe-area-inset-bottom),0px)]" data-testid="story-editor">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/40 backdrop-blur-sm">
        <button onClick={onCancel} className="p-2 -ml-2 rounded-full hover:bg-white/10" data-testid="story-editor-close">
          <X className="w-6 h-6 text-white" />
        </button>
        <p className="text-sm font-semibold text-white">Modifier la story</p>
        <button
          onClick={handlePublish}
          disabled={publishing}
          className="px-4 py-1.5 rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-600 text-white text-sm font-semibold disabled:opacity-50"
          data-testid="story-editor-publish"
        >
          {publishing ? 'Publication…' : 'Publier'}
        </button>
      </div>

      {/* Stage */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        <div ref={stageRef} className="relative w-full max-w-md aspect-[9/16] bg-black overflow-hidden" data-testid="story-editor-stage">
          {mediaType === 'image'
            ? <img src={preview} alt="" className="w-full h-full object-contain" />
            : <video src={preview} autoPlay loop muted playsInline className="w-full h-full object-contain" />
          }

          {/* Overlays */}
          {overlays.map(o => o.type === 'sticker' ? (
            <div
              key={o.id}
              onMouseDown={(e) => onPointerDown(e, o.id)}
              onTouchStart={(e) => onPointerDown(e, o.id)}
              onClick={() => setActiveId(o.id)}
              data-testid={`story-overlay-${o.id}`}
              className={`absolute select-none touch-none cursor-move ${activeId === o.id ? 'ring-2 ring-fuchsia-400 rounded-md' : ''}`}
              style={{
                left: `${o.x * 100}%`, top: `${o.y * 100}%`,
                transform: `translate(-50%, -50%) rotate(${o.rotation}deg) scale(${o.scale})`,
                fontSize: '64px',
                lineHeight: 1,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.45))',
              }}
            >
              {o.emoji}
            </div>
          ) : (
            <div
              key={o.id}
              onMouseDown={(e) => onPointerDown(e, o.id)}
              onTouchStart={(e) => onPointerDown(e, o.id)}
              onClick={() => { setActiveId(o.id); if (!o.text) setEditing(o.id); }}
              onDoubleClick={() => setEditing(o.id)}
              data-testid={`story-overlay-${o.id}`}
              className={`absolute select-none touch-none cursor-move px-2 py-1 ${activeId === o.id ? 'ring-2 ring-fuchsia-400 rounded-md' : ''}`}
              style={{
                left: `${o.x * 100}%`, top: `${o.y * 100}%`,
                transform: `translate(-50%, -50%) rotate(${o.rotation}deg) scale(${o.scale})`,
                fontSize: `${o.font_size}px`,
                fontFamily: o.font_family === 'serif' ? 'Georgia, serif'
                          : o.font_family === 'mono' ? 'monospace'
                          : 'system-ui, sans-serif',
                fontWeight: 700,
                textShadow: o.bg === 'none' ? '0 1px 2px rgba(0,0,0,0.6), 0 0 1px rgba(0,0,0,0.4)' : 'none',
                background: o.bg === 'black' ? 'rgba(0,0,0,0.55)'
                          : o.bg === 'white' ? 'rgba(255,255,255,0.9)'
                          : o.bg === 'gradient' ? 'linear-gradient(135deg, #c026d3, #7c3aed)'
                          : 'transparent',
                color: o.bg === 'white' ? (o.color === '#ffffff' ? '#000' : o.color) : o.color,
                borderRadius: o.bg === 'none' ? 0 : 8,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.15,
                maxWidth: '85%',
                wordBreak: 'break-word',
                textAlign: 'center',
              }}
            >
              {renderOverlayText(o.text, o.mentions)}
            </div>
          ))}
        </div>
      </div>

      {/* Active overlay toolbar (different controls for text vs sticker) */}
      {activeOverlay && !editing && activeOverlay.type !== 'sticker' && (
        <div className="px-3 py-2 bg-black/60 backdrop-blur-md flex items-center gap-2 overflow-x-auto" data-testid="story-editor-tools">
          {PALETTE.map(c => (
            <button
              key={c}
              onClick={() => updateOverlay(activeOverlay.id, { color: c })}
              className={`w-7 h-7 rounded-full border-2 shrink-0 ${activeOverlay.color === c ? 'border-white scale-110' : 'border-white/30'}`}
              style={{ background: c }}
              aria-label={`couleur ${c}`}
            />
          ))}
          <input
            type="range"
            min="14" max="64" step="1"
            value={activeOverlay.font_size}
            onChange={(e) => updateOverlay(activeOverlay.id, { font_size: parseInt(e.target.value, 10) })}
            className="ml-2 shrink-0 w-24 accent-fuchsia-400"
            aria-label="taille"
            data-testid="story-overlay-size"
          />
          <button
            onClick={() => updateOverlay(activeOverlay.id, {
              bg: activeOverlay.bg === 'none' ? 'black'
                : activeOverlay.bg === 'black' ? 'white'
                : activeOverlay.bg === 'white' ? 'gradient'
                : 'none',
            })}
            className="px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-xs text-white shrink-0"
            data-testid="story-overlay-bg"
          >
            Fond {activeOverlay.bg === 'none' ? '∅' : activeOverlay.bg}
          </button>
          <button
            onClick={() => setEditing(activeOverlay.id)}
            className="px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-xs text-white shrink-0"
            data-testid="story-overlay-edit"
          >
            Modifier
          </button>
          <button
            onClick={() => removeOverlay(activeOverlay.id)}
            className="px-2.5 py-1 rounded-full bg-red-500/20 hover:bg-red-500/30 text-xs text-red-300 shrink-0 flex items-center gap-1"
            data-testid="story-overlay-delete"
          >
            <Trash2 className="w-3.5 h-3.5" /> Supprimer
          </button>
        </div>
      )}

      {/* Sticker toolbar — simpler: scale slider + delete */}
      {activeOverlay && !editing && activeOverlay.type === 'sticker' && (
        <div className="px-3 py-2 bg-black/60 backdrop-blur-md flex items-center gap-3 overflow-x-auto" data-testid="story-editor-sticker-tools">
          <span className="text-xs text-white/70 shrink-0">Taille</span>
          <input
            type="range"
            min="0.5" max="3" step="0.05"
            value={activeOverlay.scale}
            onChange={(e) => updateOverlay(activeOverlay.id, { scale: parseFloat(e.target.value) })}
            className="shrink-0 w-32 accent-fuchsia-400"
            aria-label="taille"
            data-testid="story-sticker-size"
          />
          <span className="text-xs text-white/70 shrink-0 ml-1">Rotation</span>
          <input
            type="range"
            min="-180" max="180" step="5"
            value={activeOverlay.rotation}
            onChange={(e) => updateOverlay(activeOverlay.id, { rotation: parseInt(e.target.value, 10) })}
            className="shrink-0 w-32 accent-fuchsia-400"
            aria-label="rotation"
            data-testid="story-sticker-rotation"
          />
          <button
            onClick={() => removeOverlay(activeOverlay.id)}
            className="ml-auto px-2.5 py-1 rounded-full bg-red-500/20 hover:bg-red-500/30 text-xs text-red-300 shrink-0 flex items-center gap-1"
            data-testid="story-sticker-delete"
          >
            <Trash2 className="w-3.5 h-3.5" /> Supprimer
          </button>
        </div>
      )}

      {/* Add text / sticker floating buttons */}
      {!editing && (
        <div className="px-4 py-3 bg-black/40 backdrop-blur-md flex items-center justify-between gap-2">
          <p className="text-[11px] text-gray-300 hidden sm:block">Touchez pour déplacer · Double-clic pour modifier</p>
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => setShowStickers(true)}
              className="px-4 py-2 rounded-full bg-white/10 text-white border border-white/20 font-semibold text-sm flex items-center gap-1.5 hover:bg-white/20"
              data-testid="story-editor-add-sticker"
            >
              <Smile className="w-4 h-4" /> Sticker
            </button>
            <button
              onClick={addOverlay}
              className="px-4 py-2 rounded-full bg-white text-black font-semibold text-sm flex items-center gap-1.5 hover:bg-gray-200"
              data-testid="story-editor-add-text"
            >
              <Type className="w-4 h-4" /> Texte
            </button>
          </div>
        </div>
      )}

      {/* Text composer with mention autocomplete */}
      {editingOverlay && (
        <TextComposer
          overlay={editingOverlay}
          onChange={(patch) => updateOverlay(editingOverlay.id, patch)}
          onCommit={() => {
            // If user closed with empty text, drop the overlay
            const o = overlays.find(x => x.id === editingOverlay.id);
            if (!o || !(o.text || '').trim()) removeOverlay(editingOverlay.id);
            setEditing(null);
          }}
        />
      )}

      {/* Emoji sticker picker — slides in from the bottom on demand. */}
      {showStickers && (
        <StoryStickerPicker
          onPick={addSticker}
          onClose={() => setShowStickers(false)}
        />
      )}
    </div>
  );
};

// Render text with @username highlighted in fuchsia
const renderOverlayText = (text, mentions) => {
  if (!text) return null;
  const re = /(@[a-z0-9_]{3,20})/gi;
  const parts = text.split(re);
  const mentionSet = new Set((mentions || []).map(m => `@${(m.username || '').toLowerCase()}`));
  return parts.map((p, i) => {
    if (re.test(p) && mentionSet.has(p.toLowerCase())) {
      return <span key={i} style={{ color: '#f0abfc' }}>{p}</span>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

/* ====================== Text composer + @mention picker ====================== */

const TextComposer = ({ overlay, onChange, onCommit }) => {
  const [value, setValue] = useState(overlay.text || '');
  const [mentionQuery, setMentionQuery] = useState(null); // active query or null
  const [mentionResults, setMentionResults] = useState([]);
  const taRef = useRef(null);

  useEffect(() => {
    setTimeout(() => taRef.current?.focus(), 50);
  }, []);

  const detectMention = (text, caret) => {
    // Look back from caret to find an @token
    const before = text.slice(0, caret);
    const m = before.match(/@([a-z0-9_]{0,20})$/i);
    if (!m) return null;
    return { start: caret - m[0].length, query: m[1] };
  };

  const handleChange = (e) => {
    const text = e.target.value;
    setValue(text);
    onChange({ text, mentions: overlay.mentions || [] });
    const caret = e.target.selectionStart || text.length;
    const det = detectMention(text, caret);
    if (det) {
      setMentionQuery(det.query);
    } else {
      setMentionQuery(null);
      setMentionResults([]);
    }
  };

  useEffect(() => {
    if (mentionQuery === null) return;
    const q = mentionQuery;
    const handle = setTimeout(async () => {
      try {
        const r = await axios.get(`/api/users/mentions/search?q=${encodeURIComponent(q)}`);
        setMentionResults(r.data?.users || []);
      } catch { setMentionResults([]); }
    }, 200);
    return () => clearTimeout(handle);
  }, [mentionQuery]);

  const insertMention = (u) => {
    // Replace the current @… token with @username + space, and record mention
    const caret = taRef.current?.selectionStart || value.length;
    const before = value.slice(0, caret);
    const after = value.slice(caret);
    const tokenStart = before.lastIndexOf('@');
    if (tokenStart < 0) return;
    const next = before.slice(0, tokenStart) + `@${u.username} ` + after;
    setValue(next);
    const newMentions = [
      ...(overlay.mentions || []).filter(m => m.username !== u.username),
      { username: u.username, user_id: u.id },
    ];
    onChange({ text: next, mentions: newMentions });
    setMentionQuery(null);
    setMentionResults([]);
    setTimeout(() => {
      taRef.current?.focus();
      const newCaret = tokenStart + u.username.length + 2;
      taRef.current?.setSelectionRange(newCaret, newCaret);
    }, 10);
  };

  return (
    <div className="absolute inset-0 z-10 bg-black/85 backdrop-blur-sm flex flex-col" data-testid="story-text-composer">
      <div className="flex items-center justify-between px-4 py-3">
        <button onClick={onCommit} className="text-white text-sm" data-testid="story-composer-cancel">Annuler</button>
        <p className="text-white text-sm font-semibold flex items-center gap-1.5">
          <AtSign className="w-4 h-4 text-fuchsia-300" /> Texte & mentions
        </p>
        <button onClick={onCommit} className="text-fuchsia-300 text-sm font-semibold flex items-center gap-1" data-testid="story-composer-done">
          <Check className="w-4 h-4" /> OK
        </button>
      </div>

      <div className="flex-1 px-4 flex flex-col items-center justify-center">
        <textarea
          ref={taRef}
          value={value}
          onChange={handleChange}
          placeholder="Écrivez quelque chose… Tapez @ pour mentionner"
          rows={3}
          maxLength={200}
          className="w-full max-w-md bg-transparent text-white text-center text-2xl font-bold placeholder:text-white/40 outline-none resize-none"
          style={{ caretColor: overlay.color }}
          data-testid="story-composer-textarea"
        />
        <p className="text-[11px] text-white/50 mt-2">{value.length}/200</p>
      </div>

      {mentionResults.length > 0 && (
        <div className="bg-black/70 border-t border-white/10 max-h-56 overflow-y-auto" data-testid="story-mention-results">
          {mentionResults.map(u => (
            <button
              key={u.id}
              onClick={() => insertMention(u)}
              className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-white/10 text-left"
              data-testid={`story-mention-${u.username}`}
            >
              <div className="w-9 h-9 rounded-full overflow-hidden bg-white/10 shrink-0">
                {u.profile_image_url
                  ? <img src={u.profile_image_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-white font-bold">{(u.username || '?')[0]?.toUpperCase()}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{u.name || u.username}</p>
                <p className="text-xs text-white/60 truncate">@{u.username}{u.is_verified && ' ✓'}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default StoryEditor;
